/**
 * AI Training Types
 * 
 * TypeScript interfaces for AI-AMSO voice training system.
 */

// =============================================================================
// ENUMS
// =============================================================================

export enum TrainingSessionStatus {
    DRAFT = 'DRAFT',
    ACTIVE = 'ACTIVE',
    PAUSED = 'PAUSED',
    COMPLETED = 'COMPLETED',
    TRAINING = 'TRAINING',
    FAILED = 'FAILED',
}

export enum ATCAction {
    HOLD = 'HOLD',
    TAXI = 'TAXI',
    PUSHBACK = 'PUSHBACK',
    EXPEDITE = 'EXPEDITE',
    CLEARED = 'CLEARED',
    GIVE_WAY = 'GIVE_WAY',
    CROSS = 'CROSS',
    STOP = 'STOP',
    CONTINUE = 'CONTINUE',
    UNKNOWN = 'UNKNOWN',
}

export enum CommandReason {
    CONFLICT = 'CONFLICT',
    PRIORITY = 'PRIORITY',
    WEATHER = 'WEATHER',
    SLOT = 'SLOT',
    TRAFFIC = 'TRAFFIC',
    GROUND_VEHICLE = 'GROUND_VEHICLE',
    NONE = 'NONE',
}

export enum ValidationStatus {
    PENDING = 'PENDING',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    CORRECTED = 'CORRECTED',
}

// =============================================================================
// PARSED COMMAND
// =============================================================================

export interface ParsedCommand {
    action: ATCAction;
    target_flight: string | null;
    location_node: string | null;
    reason: CommandReason;
    confidence: number;
    raw_text: string;
}

// =============================================================================
// VOICE COMMAND LOG
// =============================================================================

export interface VoiceCommandLog {
    id: string;
    session_id: string;

    // Audio info
    audio_duration_ms: number | null;

    // Transcription
    transcription: string;

    // Parsed fields
    parsed_action: ATCAction;
    parsed_target: string | null;
    parsed_location: string | null;
    parsed_reason: string | null;
    confidence: number;

    // Validation
    is_validated: string;
    corrected_action: string | null;

    // Timestamps
    captured_at: string;
    simulation_time: string | null;
}

export interface VoiceCommandValidation {
    status: ValidationStatus;
    corrected_action?: string;
    corrected_target?: string;
    corrected_location?: string;
    notes?: string;
}

// =============================================================================
// TRAINING SESSION
// =============================================================================

export interface AITrainingSession {
    id: string;
    name: string;
    description: string | null;
    project_id: string;

    // Stats
    total_commands: number;
    valid_commands: number;
    status: TrainingSessionStatus;

    // Model
    model_path: string | null;
    model_accuracy: number | null;

    // Timestamps
    started_at: string | null;
    completed_at: string | null;
    created_at: string;
}

export interface AITrainingSessionDetail extends AITrainingSession {
    recent_commands: VoiceCommandLog[];
    training_config: Record<string, unknown> | null;
}

export interface CreateTrainingSessionRequest {
    name: string;
    description?: string;
    project_id: string;
    training_days?: string[];
    simulation_scenario_id?: string;
    training_config?: Record<string, unknown>;
}

export interface UpdateTrainingSessionRequest {
    name?: string;
    description?: string;
    status?: TrainingSessionStatus;
    training_days?: string[];
    training_config?: Record<string, unknown>;
}

// =============================================================================
// WEBSOCKET MESSAGES
// =============================================================================

export interface WebSocketMessage {
    type: string;
}

export interface WebSocketAudioMessage extends WebSocketMessage {
    type: 'audio' | 'text';
    data: string;
    airport_state?: AirportStateSnapshot;
}

export interface WebSocketTranscriptionResult extends WebSocketMessage {
    type: 'transcription';
    transcription: string;
    command: ParsedCommand;
    is_final: boolean;
    command_id: string | null;
}

export interface WebSocketStatusMessage extends WebSocketMessage {
    type: 'status';
    status: string;
    message: string | null;
}

export interface WebSocketErrorMessage extends WebSocketMessage {
    type: 'error';
    code: string;
    message: string;
}

// =============================================================================
// AIRPORT STATE SNAPSHOT
// =============================================================================

export interface AircraftSnapshot {
    flight_id: string;
    position: [number, number];
    heading: number;
    phase: string;
    stand: string | null;
}

export interface AirportStateSnapshot {
    timestamp: string;
    aircraft: AircraftSnapshot[];
    gse_vehicles?: unknown[];
    weather?: Record<string, unknown>;
}

// =============================================================================
// TRANSCRIPTION LOG (for UI)
// =============================================================================

export interface TranscriptionLog {
    id: string;
    transcription: string;
    command: ParsedCommand;
    timestamp: Date;
    isValidated: boolean;
}
